import type { Observable } from 'rxjs';
import type { PagedList } from '../data/models/paged-list.model';
import type {
  DocumentDownloadInfo,
  ProviderDocument,
} from '../data/models/document.model';
import type {
  Provider,
  ProviderOptimized,
  Tier3Provider,
} from '../data/models/provider.model';
import type {
  ProviderNote,
  ProviderNoteComment,
} from '../data/models/provider-note.model';
import type { SortModel } from '../data/models/sort-model.model';
import httpService from './httpService';
import type { BaseTripPreferences } from '../data/models/trip-preferences.model';
import type { CoverageArea } from '../data/models/coverage-area.model';
import type {
  Tier3TripLegAttestation,
  TripLegAttestation,
} from '../data/models/trip-leg.model';

class ProviderService {
  private readonly apiBase;
  private readonly accountingApiBase;
  constructor() {
    const { __environment__ } = window as any;
    this.apiBase = __environment__.REACT_APP_PROVIDER_API_URL.trim();
    this.accountingApiBase =
      __environment__.REACT_APP_PUBLIC_ACCOUNTING_API_URL.trim();
  }

  private convertFilter(filter: string): string {
    const parts = filter.split('&').sort();
    const cttParts = parts.filter((p) => p.startsWith('ctt'));
    const insParts = parts.filter((p) => p.startsWith('ins'));
    const svcParts = parts.filter((p) => p.startsWith('svc'));
    const activeParts = parts.filter((p) => p.startsWith('active'));
    let f = '';

    if (cttParts.length === 1) {
      f += 'ctt-' + cttParts[0].substring(3);
    }
    if (insParts.length === 1) {
      if (f.length) {
        f += '~';
      }
      f += 'ins-' + insParts[0].substring(3);
    }
    if (svcParts.length > 0) {
      if (f.length) {
        f += '~';
      }
      f += 'svc';
      svcParts.forEach((svc) => {
        f += '-' + svc.substring(3);
      });
    }
    if (activeParts.length === 1) {
      if (f.length) {
        f += '~';
      }
      f += 'active-' + activeParts[0].substring(6);
    }
    return f;
  }

  public findProviders(
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    tripModeIDs?: number[],
    coverageAreaState?: string[],
    coverageAreaEffectiveOn?: string,
  ): Observable<PagedList<ProviderOptimized>> {
    const f = encodeURIComponent(this.convertFilter(filter));
    const s = encodeURIComponent(search);
    let url = `${this.apiBase}/optimized?page=${page}&pageSize=${pageSize}&filters=${f}&searchProviders=${s}`;
    // let url = `${this.apiBase}/optimized?page=${page}&pageSize=${pageSize}&searchProviders=${s}`;
    if (sortModel) {
      url += `&sort=${sortModel.column}&sortOrder=${sortModel.direction}`;
    }
    if (tripModeIDs && tripModeIDs.length > 0) {
      url += `&tripModeIDs=${encodeURIComponent(tripModeIDs.join(','))}`;
    }
    if (coverageAreaState && coverageAreaState.length > 0) {
      url += `&coverageAreaState=${encodeURIComponent(
        coverageAreaState.join(','),
      )}`;
    }
    if (coverageAreaEffectiveOn !== undefined) {
      url += `&coverageAreaEffectiveOn=${encodeURIComponent(
        coverageAreaEffectiveOn,
      )}`;
    }
    return httpService.getJSON(url);
  }

  public getProvider(
    id: number,
    coverageAreaState?: string[],
    coverageAreaEffectiveOn?: string,
  ): Observable<Provider> {
    let url = `${this.apiBase}/${id}`;
    const params: string[] = [];

    if (coverageAreaState && coverageAreaState.length > 0) {
      params.push(
        `coverageAreaState=${encodeURIComponent(coverageAreaState.join(','))}`,
      );
    }
    if (coverageAreaEffectiveOn !== undefined) {
      params.push(
        `coverageAreaEffectiveOn=${encodeURIComponent(
          coverageAreaEffectiveOn,
        )}`,
      );
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return httpService.getJSON(url);
  }

  public createProvider(provider: Provider): Observable<Provider> {
    const url = this.apiBase;
    return httpService.postJSON(url, provider);
  }

  public updateProvider(
    provider: Provider | Partial<Provider>,
  ): Observable<Provider> {
    const url = `${this.apiBase}/${provider.transportationProviderID}`;
    return httpService.putJSON(url, provider);
  }

  public deleteProvider(providerId: number): Observable<Provider> {
    const url = `${this.apiBase}/${providerId}`;
    return httpService.deleteJSON(url);
  }

  public createTier3Provider(
    provider: Tier3Provider,
  ): Observable<Tier3Provider> {
    const url = `${this.apiBase}/tier3`;
    return httpService.postJSON(url, provider);
  }

  public updateTier3Provider(
    provider: Tier3Provider,
  ): Observable<Tier3Provider> {
    const url = `${this.apiBase}/tier3/${provider.transportationProviderID}`;
    return httpService.putJSON(url, provider);
  }

  public uploadProviderDocument(
    providerId: number,
    document: ProviderDocument,
    file: any,
  ): Observable<ProviderDocument> {
    const url = `${this.apiBase}/${providerId}/documents`;
    const formData = new FormData();
    formData.append('document', file, file.name);
    formData.append(
      'transportationProviderDocumentTypeID',
      '' + document.transportationProviderDocumentTypeID,
    );
    if (document.signedOnDate) {
      formData.append('signedOnDate', document.signedOnDate);
    }
    if (document.effectiveStartDate) {
      formData.append('effectiveStartDate', document.effectiveStartDate);
    }
    if (document.effectiveEndDate) {
      formData.append('effectiveEndDate', document.effectiveEndDate);
    }

    return httpService.postJSON(url, formData, true);
  }

  public updateProviderDocument(
    providerId: number,
    document: ProviderDocument,
  ): Observable<ProviderDocument> {
    const url = `${this.apiBase}/${providerId}/documents/${document.transportationProviderDocumentID}`;
    return httpService.putJSON(url, document);
  }

  public deleteProviderDocument(
    providerId: number,
    documentId: number,
  ): Observable<ProviderDocument> {
    const url = `${this.apiBase}/${providerId}/documents/${documentId}`;
    return httpService.deleteJSON(url);
  }

  public getProviderDocuments(
    providerId: number,
  ): Observable<ProviderDocument[]> {
    const url = `${this.apiBase}/${providerId}/documents`;
    return httpService.getJSON(url);
  }

  public getProviderNotes(providerId: number): Observable<ProviderNote[]> {
    const url = `${this.apiBase}/${providerId}/notes`;
    return httpService.getJSON(url);
  }

  public createProviderNote(
    providerId: number,
    note: ProviderNote,
  ): Observable<ProviderNote> {
    const url = `${this.apiBase}/${providerId}/notes`;
    return httpService.postJSON(url, note);
  }

  public updateProviderNote(
    providerId: number,
    note: ProviderNote,
  ): Observable<ProviderNote> {
    const url = `${this.apiBase}/${providerId}/notes/${note.transportationProviderNoteID}`;
    return httpService.putJSON(url, note);
  }

  public deleteProviderNote(
    providerId: number,
    noteId: number,
  ): Observable<void> {
    const url = `${this.apiBase}/${providerId}/notes/${noteId}`;
    return httpService.deleteJSON(url);
  }

  public getNoteComments(
    providerId: number,
    noteId: number,
  ): Observable<ProviderNoteComment[]> {
    const url = `${this.apiBase}/${providerId}/notes/${noteId}/comments`;
    return httpService.getJSON(url);
  }

  public createNoteComment(
    providerId: number,
    noteId: number,
    note: ProviderNoteComment,
  ): Observable<ProviderNoteComment> {
    const url = `${this.apiBase}/${providerId}/notes/${noteId}/comments`;
    return httpService.postJSON(url, note);
  }

  public updateNoteComment(
    providerId: number,
    note: ProviderNoteComment,
  ): Observable<ProviderNoteComment> {
    const url = `${this.apiBase}/${providerId}/notes/${note.transportationProviderNoteID}/comments/${note.transportationProviderNoteCommentID}`;
    return httpService.putJSON(url, note);
  }

  public deleteNoteComment(
    providerId: number,
    noteId: number,
    commentId: number,
  ): Observable<void> {
    const url = `${this.apiBase}/${providerId}/notes/${noteId}/comments/${commentId}`;
    return httpService.deleteJSON(url);
  }

  public getTripPreferences(
    providerId: number,
  ): Observable<BaseTripPreferences> {
    const url = `${this.apiBase}/${providerId}/tripPreferences`;

    return httpService.getJSON(url);
  }

  public updateTripPreferences(
    providerId: number,
    tripPreferences: BaseTripPreferences,
  ): Observable<BaseTripPreferences> {
    const url = `${this.apiBase}/${providerId}/tripPreferences`;

    return httpService.putJSON(url, tripPreferences);
  }

  public updateProviderOnboardingUser(
    transportationProviderUserOnboardingID: number,
    onboardingUser: Object,
  ): Observable<void> {
    const url = `${this.apiBase}/onboarding/${transportationProviderUserOnboardingID}`;

    return httpService.putJSON(url, onboardingUser);
  }

  public getCoverageAreas(): Observable<{
    filter: any;
    coverageAreas: CoverageArea[];
  }> {
    const url = `${this.apiBase}/coverageAreas`;

    return httpService.getJSON(url);
  }

  public attestTier3TripLeg(leg: Tier3TripLegAttestation): Observable<void> {
    const url = `${this.accountingApiBase}/provider-invoice`;
    return httpService.postJSON(url, leg);
  }

  public getDocumentDownloadInfo(
    providerID: number,
    documentID: number,
  ): Observable<DocumentDownloadInfo> {
    const url = `${this.apiBase}/${providerID}/documents/${documentID}`;
    return httpService.getJSON(url);
  }
}

const providerService = new ProviderService();

export default providerService;
